import PropTypes from "prop-types";
import React from "react";
import { X } from "lucide-react";
import 'antd/dist/reset.css';
import { Modal, App, Button, Flex } from "antd";

const DialogContext = React.createContext({
    open: false,
    onOpenChange: undefined,
});

function composeHandlers(userHandler, internalHandler) {
    return (event) => {
        userHandler?.(event);
        if (!event?.defaultPrevented) {
            internalHandler?.(event);
        }
    };
}

// Базовая обёртка: хранит состояние и отдаёт его составным частям
export function UiDialogRoot({ open, onOpenChange, children }) {
    const value = React.useMemo(
        () => ({ open: Boolean(open), onOpenChange }),
        [open, onOpenChange]
    );

    return <DialogContext.Provider value={value}>{children}</DialogContext.Provider>;
}

export const UiDialogTrigger = React.forwardRef(({ onClick, ...props }, ref) => {
    const { onOpenChange } = React.useContext(DialogContext);

    return (
        <Button
            type="button"
            ref={ref}
            onClick={composeHandlers(onClick, () => onOpenChange?.(true))}
            {...props}
        />
    );
});
UiDialogTrigger.displayName = "UiDialogTrigger";

export function UiDialogPortal({ children }) {
    return <>{children}</>;
}

// Совместимость API: overlay не нужен отдельно, так как его рендерит Modal
export const UiDialogOverlay = React.forwardRef((props, ref) => (
    <div ref={ref} {...props} />
));
UiDialogOverlay.displayName = "UiDialogOverlay";

// Контент диалога на базе antd Modal с сохранением внешнего API
export const UiDialogContent = React.forwardRef(
    ({ children, className = "", size = "lg", ...rest }, ref) => {
        const { open, onOpenChange } = React.useContext(DialogContext);

        const sizeMap = {
            sm: 480,
            md: 720,
            lg: 960,
            xl: 1200,
            full: 1400,
        };

        return (
            <App>
            <Modal
                open={open}
                onCancel={() => onOpenChange?.(false)}
                footer={null}
                title={null}
                closable={false}
                destroyOnHidden
                centered
                width={sizeMap[size] ?? sizeMap.lg}
                className={className}
                styles={{
                    body: { padding: 0 },
                    content: { overflow: "hidden" },
                }}
                {...rest}
            >
                <div
                    ref={ref}
                    className="max-h-[80vh] overflow-hidden bg-white dark:bg-zinc-900 flex flex-col"
                >
                    {children}
                </div>
            </Modal>
            </App>
        );
    }
);
UiDialogContent.displayName = "UiDialogContent";

// New: Sticky dialog header
export const UiDialogHeader = React.forwardRef(
    ({ children, className = "", ...props }, ref) => (
        <div
            ref={ref}
            className={
                "flex items-start justify-between gap-4 p-6 pb-4 border-b border-zinc-200 dark:border-zinc-800 " +
                className
            }
            {...props}
        >
            {children}
        </div>
    )
);
UiDialogHeader.displayName = "UiDialogHeader";

// New: Scrollable dialog body
export const UiDialogBody = React.forwardRef(
    ({ children, className = "", ...props }, ref) => (
        <div
            ref={ref}
            className={"flex-1 overflow-y-auto p-6 " + className}
            {...props}
        >
            {children}
        </div>
    )
);
UiDialogBody.displayName = "UiDialogBody";

// New: Sticky dialog footer
export const UiDialogFooter = React.forwardRef(
    ({ children, className = "", ...props }, ref) => (
        <Flex
            flex
            align="flex-end"
            justify="flex-end"
            gap="medium"
            wrap="wrap"            
            ref={ref}
            className={
                "p-6 pt-4 border-t border-zinc-200 dark:border-zinc-800 " +
                className
            }
            {...props}
        >
            {children}
        </Flex>
    )
);
UiDialogFooter.displayName = "UiDialogFooter";

export const UiDialogTitle = React.forwardRef((props, ref) => (
    <h2
        ref={ref}
        className="text-xl font-semibold text-zinc-900 dark:text-zinc-100"
        {...props}
    />
));
UiDialogTitle.displayName = "UiDialogTitle";

export const UiDialogDescription = React.forwardRef((props, ref) => (
    <p
        ref={ref}
        className="text-sm text-zinc-500 dark:text-zinc-400"
        {...props}
    />
));
UiDialogDescription.displayName = "UiDialogDescription";

// Enhanced close button with icon
export const UiDialogClose = React.forwardRef(
    ({ children, className = "", showIcon = false, onClick, ...props }, ref) => {
        const { onOpenChange } = React.useContext(DialogContext);

        return (
        <Button
            type="text"
            ref={ref}
            icon={<X size={20} />}
            className={
                showIcon
                    ? "rounded-lg p-2 text-zinc-500 hover:bg-zinc-100 dark:hover:bg-zinc-800 transition-colors " +
                    className
                    : className
            }
            onClick={composeHandlers(onClick, () => onOpenChange?.(false))}
            {...props}
        >
        </Button>
    );}
);
UiDialogClose.displayName = "UiDialogClose";

// PropTypes
UiDialogRoot.propTypes = {
    open: PropTypes.bool,
    onOpenChange: PropTypes.func,
    children: PropTypes.node,
};
UiDialogPortal.propTypes = {
    children: PropTypes.node,
};
UiDialogContent.propTypes = {
    children: PropTypes.node,
    className: PropTypes.string,
    size: PropTypes.oneOf(["sm", "md", "lg", "xl", "full"]),
};
UiDialogHeader.propTypes = {
    children: PropTypes.node,
    className: PropTypes.string,
};
UiDialogBody.propTypes = {
    children: PropTypes.node,
    className: PropTypes.string,
};
UiDialogFooter.propTypes = {
    children: PropTypes.node,
    className: PropTypes.string,
};
UiDialogClose.propTypes = {
    children: PropTypes.node,
    className: PropTypes.string,
    showIcon: PropTypes.bool,
};
UiDialogOverlay.propTypes =
    UiDialogTrigger.propTypes =
    UiDialogTitle.propTypes =
    UiDialogDescription.propTypes =
    {
        children: PropTypes.node,
    };
